/* Wizard of Barge — Smart Cart Package Protection (no auto-open)
   Auto-adds the correct protection tier (opt-out allowed) *only while the
   Rebuy Smart Cart is open*. Requires a Custom HTML block in Smart Cart:
     <div id="wob-protection-toggle" data-wob-protection></div>
*/
(() => {
  // ===== CONFIG =====
  const PROTECTION_PRODUCT_HANDLE = 'wob-shipping-protection'; // product handle
  const PROTECTION_SKU_PREFIX     = 'WOBNVDPROTECTION';        // SKUs: …1, …2, …3, etc.
  const MAX_TIER        = 100;   // …1 → …100
  const TIER_STEP_CENTS = 3000;  // new tier every $30
  const OPT_OUT_KEY     = 'wob_protection_optout';
  const MOUNT_SELECTOR  = '[data-wob-protection]';
  const LABEL           = 'Package Protection';
  const SUBTXT          = 'Covers loss, damage, or goblin theft during transit.';
  // ===================

  let productCache = null; // cache /products/<handle>.js

  // ---- Helpers: Ajax ----
  const getCart = () =>
    fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());

  const addItem = (variantId) =>
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: { _wob_protection: '1' }
      })
    }).then(r => r.json());

  const changeCart = (payload) =>
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(r => r.json());

  // ---- Helpers: product JSON (SKU → variant id) ----
  async function getProtectionProduct() {
    if (productCache) return productCache;
    const res = await fetch(`/products/${PROTECTION_PRODUCT_HANDLE}.js`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('[WOB Protection] Could not load product JSON. Check handle & sales channel.');
    productCache = await res.json();
    return productCache;
  }

  async function variantIdForTier(tierNum) {
    const prod = await getProtectionProduct();
    const targetSku = `${PROTECTION_SKU_PREFIX}${tierNum}`.toUpperCase();
    const variant = prod.variants.find(v => (v.sku || '').toUpperCase() === targetSku);
    if (!variant) {
      console.warn('[WOB Protection] No variant for', targetSku, '— check SKU spelling or publish status.');
      return null;
    }
    return variant.id;
  }

  // ---- Helpers: cart lines & math ----
  function isProtectionLine(item) {
    const flagged  = item.properties && item.properties._wob_protection === '1';
    const skuMatch = typeof item.sku === 'string' && item.sku.toUpperCase().startsWith(PROTECTION_SKU_PREFIX);
    return flagged || skuMatch;
  }
  function findProtectionLine(cart) {
    return cart.items.find(isProtectionLine);
  }
  function subtotalExcludingProtection(cart) {
    // Shopify returns cents
    return cart.items.reduce((sum, i) => isProtectionLine(i) ? sum : sum + i.final_line_price, 0);
  }
  // 0–3000 => 1, 3000–6000 => 2, …, cap 1..MAX_TIER
  function tierNumberForSubtotal(subCents) {
    const n = Math.floor(subCents / TIER_STEP_CENTS) + 1;
    return Math.min(Math.max(n, 1), MAX_TIER);
  }

  // ---- Opt-out flag ----
  const getOptOut = () => localStorage.getItem(OPT_OUT_KEY) === '1';
  const setOptOut = (v) => localStorage.setItem(OPT_OUT_KEY, v ? '1' : '0');

  // ---- Detect if Smart Cart is actually open ----
  function isSmartCartOpen() {
    // Rebuy commonly uses these
    const el = document.querySelector('#rebuy-cart, .rebuy-cart');
    if (!el) return false;
    const aria = el.getAttribute('aria-hidden');
    if (aria === 'false') return true;
    const cls = el.classList;
    if (cls.contains('is-open') || cls.contains('open') || cls.contains('rebuy-cart--open')) return true;
    // Some themes add an open flag to <body>
    const b = document.body.classList;
    if (b.contains('rebuy-cart-open') || b.contains('rebuy-is-open')) return true;
    return false;
  }

  // ---- UI row in the drawer ----
  function renderToggle(checked) {
    const mount = document.querySelector(MOUNT_SELECTOR);
    if (!mount) return;
    mount.innerHTML = `
      <div class="wob-prot-row" style="display:flex;gap:.75rem;align-items:center;justify-content:space-between;margin:.75rem 0;padding:.75rem;border:1px solid #e5e5e5;border-radius:.75rem;">
        <div style="display:flex;flex-direction:column;">
          <span style="font-weight:600;">${LABEL}</span>
          <span style="font-size:.9rem;opacity:.8;">${SUBTXT}</span>
        </div>
        <label style="display:inline-flex;align-items:center;gap:.5rem;cursor:pointer;">
          <input type="checkbox" id="wob-prot-toggle" ${checked ? 'checked' : ''} aria-label="Toggle package protection">
        </label>
      </div>
    `;
    const input = mount.querySelector('#wob-prot-toggle');
    input?.addEventListener('change', async (e) => {
      const want = e.target.checked;
      if (!want) {
        setOptOut(true);
        const cart = await getCart();
        const line = findProtectionLine(cart);
        if (line) await changeCart({ updates: { [line.key]: 0 } });
      } else {
        setOptOut(false);
        ensureProtection(); // cart is open when user toggles
      }
    });
  }

  // ---- Controller (mutates cart only when drawer is open) ----
  async function ensureProtection() {
    const mount = document.querySelector(MOUNT_SELECTOR);
    if (!mount) return;

    const cart   = await getCart();
    const open   = isSmartCartOpen();
    const line   = findProtectionLine(cart);
    const optedOut = getOptOut();

    // Always reflect current state in the UI
    renderToggle(!optedOut && !!line);

    // If drawer isn't open, DON'T mutate the cart (prevents auto-open)
    if (!open) return;

    // Gift card–only carts: never include protection
    const onlyGiftCards = cart.items.length > 0 && cart.items.every(i => i.gift_card === true);
    if (onlyGiftCards || optedOut) {
      if (line) await changeCart({ updates: { [line.key]: 0 } });
      return;
    }

    // Compute desired tier for current subtotal (excluding protection)
    const sub      = subtotalExcludingProtection(cart);
    const tierNum  = tierNumberForSubtotal(sub);
    const desiredId = await variantIdForTier(tierNum);

    if (!line) {
      if (desiredId) await addItem(desiredId);
      return;
    }

    // Adjust tier if needed
    if (String(line.variant_id) !== String(desiredId)) {
      await changeCart({ updates: { [line.key]: 0 } });
      if (desiredId) await addItem(desiredId);
    }
  }

  // ---- Wiring ----
  function init() {
    // When Smart Cart opens, ensure protection is correct (allowed to mutate)
    document.addEventListener('rebuy:smartcart.show', ensureProtection);

    // While open, keep tiers in sync as cart changes
    document.addEventListener('rebuy:cart.change', () => {
      if (isSmartCartOpen()) ensureProtection();
    });

    // If the drawer happens to be open already, sync once
    if (isSmartCartOpen()) ensureProtection();
  }

  document.addEventListener('DOMContentLoaded', init);
})();
